import React, { useState, useMemo } from 'react';
import { ShoppingBag, ExternalLink, Download, Eye, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { toast } from 'sonner';
import { TRUCKSHOP_LOGO, SHOPIFY_LOGO } from '../lib/logos';

const MONTHS = [
  { value: '01', label: 'Januari', short: 'jan' },
  { value: '02', label: 'Februari', short: 'feb' },
  { value: '03', label: 'Maart', short: 'mrt' },
  { value: '04', label: 'April', short: 'apr' },
  { value: '05', label: 'Mei', short: 'mei' },
  { value: '06', label: 'Juni', short: 'jun' },
  { value: '07', label: 'Juli', short: 'jul' },
  { value: '08', label: 'Augustus', short: 'aug' },
  { value: '09', label: 'September', short: 'sep' },
  { value: '10', label: 'Oktober', short: 'okt' },
  { value: '11', label: 'November', short: 'nov' },
  { value: '12', label: 'December', short: 'dec' },
];

const currentYear = new Date().getFullYear();
const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');

function parseBedrag(val) {
  if (!val || val === '') return 0;
  const cleaned = String(val).replace(/[^\d.,-]/g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
}

function fmtEur(n) {
  return n.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

export default function TruckshopMemoPage() {
  const [maand, setMaand] = useState(currentMonth);
  const [jaar, setJaar] = useState(String(currentYear));
  const [kostprijs, setKostprijs] = useState('');
  const [b2bOmzet, setB2bOmzet] = useState('');
  const [astenOmzet, setAstenOmzet] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [exporting, setExporting] = useState(false);

  const mndLabel = MONTHS.find(m => m.value === maand)?.short || 'jan';
  const jrLabel = jaar.slice(-2);
  const periodeLabel = `${mndLabel}-${jrLabel}`;

  const journaalRegels = useMemo(() => {
    const rows = [];
    const kp = round2(parseBedrag(kostprijs));
    const b2b = round2(parseBedrag(b2bOmzet));
    const asten = round2(parseBedrag(astenOmzet));

    // Sectie 1: Voorraadmutatie
    if (kp > 0) {
      rows.push({
        sectie: 'Voorraadmutatie',
        grootboek: '3500',
        omschrijving: `voorraadmutatie ${periodeLabel}`,
        debet: kp,
        credit: 0,
        kostenplaats: '',
      });
      rows.push({
        sectie: 'Voorraadmutatie',
        grootboek: '1901',
        omschrijving: `voorraadmutatie ${periodeLabel}`,
        debet: 0,
        credit: kp,
        kostenplaats: '',
      });
    }

    // Sectie 2: Omzetverdeling Webshop
    if (b2b > 0) {
      rows.push({
        sectie: 'Omzetverdeling',
        grootboek: '8700',
        omschrijving: `Omzet Onderling ${periodeLabel}`,
        debet: b2b,
        credit: 0,
        kostenplaats: 'WEBSHOP',
      });
      rows.push({
        sectie: 'Omzetverdeling',
        grootboek: '6700',
        omschrijving: `Omzet Onderling ${periodeLabel}`,
        debet: 0,
        credit: b2b,
        kostenplaats: 'TRUCKSHO',
      });
    }
    if (asten > 0) {
      rows.push({
        sectie: 'Omzetverdeling',
        grootboek: '8701',
        omschrijving: `omzet webshop asten ${periodeLabel}`,
        debet: asten,
        credit: 0,
        kostenplaats: 'WEBSHOP',
      });
      rows.push({
        sectie: 'Omzetverdeling',
        grootboek: '8701',
        omschrijving: `omzet webshop asten ${periodeLabel}`,
        debet: 0,
        credit: asten,
        kostenplaats: 'ASTEN',
      });
    }

    return rows;
  }, [kostprijs, b2bOmzet, astenOmzet, periodeLabel]);

  const hasInput = parseBedrag(kostprijs) > 0 || parseBedrag(b2bOmzet) > 0 || parseBedrag(astenOmzet) > 0;

  const handleExport = async () => {
    if (journaalRegels.length === 0) return;
    setExporting(true);
    try {
      const datumStr = `01-${maand}-${jaar}`;

      // Split voorraad (Format 1) and omzet (Format 2) rows
      const voorraadRows = journaalRegels.filter(r => r.sectie === 'Voorraadmutatie').map(r => ({
        datum: datumStr,
        grootboek: r.grootboek,
        bedrag: r.debet > 0 ? r.debet : -(r.credit || 0),
        omschrijving: r.omschrijving,
      }));
      const omzetRows = journaalRegels.filter(r => r.sectie === 'Omzetverdeling').map(r => ({
        datum: datumStr,
        grootboek: r.grootboek,
        bedrag: r.debet > 0 ? r.debet : -(r.credit || 0),
        omschrijving: r.omschrijving,
        kostenplaats: r.kostenplaats,
      }));

      // Determine format: if only voorraad → Format 1, if only omzet → Format 2, if both → combine both calls
      const allRows = [];
      let fmt = 'memoriaal';

      if (voorraadRows.length > 0 && omzetRows.length === 0) {
        allRows.push(...voorraadRows);
        fmt = 'memoriaal';
      } else if (omzetRows.length > 0 && voorraadRows.length === 0) {
        allRows.push(...omzetRows);
        fmt = 'memoriaal_kp';
      } else {
        // Both present: export voorraad as Format 1, omzet as Format 2 in one combined Format 2
        // (Kostenplaats empty for voorraad rows is acceptable)
        voorraadRows.forEach(r => { r.kostenplaats = ''; });
        allRows.push(...voorraadRows, ...omzetRows);
        fmt = 'memoriaal_kp';
      }

      const filename = `Truckshop1_Memo_DB90_${periodeLabel}.xls`;
      const res = await api.post('/api/truckshop-memo/export', {
        rows: allRows,
        format: fmt,
        filename,
        metadata: {
          periode: periodeLabel,
          boekjaar: parseInt(jaar),
          dagboek: 90,
          regels: journaalRegels.length,
          voorraadmutatie: round2(parseBedrag(kostprijs)),
          b2b_omzet: round2(parseBedrag(b2bOmzet)),
          asten_omzet: round2(parseBedrag(astenOmzet)),
        },
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} gedownload en gelogd`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export mislukt');
    } finally {
      setExporting(false);
    }
  };

  const totaalDebet = round2(journaalRegels.reduce((s, r) => s + r.debet, 0));
  const totaalCredit = round2(journaalRegels.reduce((s, r) => s + r.credit, 0));

  return (
    <div className="tw1-fade-in">
      {/* Hero Banner — dark with pink accent */}
      <div className="rounded-2xl px-6 py-5 mb-6" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d1a2e 60%, #1a1a1a 100%)' }} data-testid="truckshop-memo-hero">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(233,30,140,0.18)' }}>
              <ShoppingBag className="w-5 h-5 text-[#e91e8c]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">Truckshop 1 Memo</h1>
              <p className="text-sm text-gray-400 mt-0.5">Maandelijkse memoriaalboekingen — Dagboek 90</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <img src={TRUCKSHOP_LOGO} alt="Truckshop" className="hidden sm:block h-8 w-auto opacity-80" style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.3))' }} />
            <img src={SHOPIFY_LOGO} alt="Shopify" className="hidden sm:block h-8 w-auto opacity-80" style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.3))' }} />
            <Button
              data-testid="open-shopify-btn"
              onClick={() => window.open('https://admin.shopify.com', '_blank')}
              className="tw1-btn text-xs"
              style={{ background: '#e91e8c', color: '#fff' }}
              size="sm"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open Shopify
            </Button>
          </div>
        </div>
      </div>

      {/* Periode selector */}
      <div className="tw1-card p-5 mb-6" data-testid="periode-selector">
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="w-4 h-4 text-[#e91e8c]" />
          <span className="text-sm font-semibold text-[#0b1220]">Periode</span>
        </div>
        <div className="flex gap-4">
          <div className="w-48">
            <Label className="text-xs text-[#526071] mb-1.5 block">Maand</Label>
            <Select value={maand} onValueChange={setMaand}>
              <SelectTrigger data-testid="memo-maand-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-32">
            <Label className="text-xs text-[#526071] mb-1.5 block">Jaar</Label>
            <Input
              data-testid="memo-jaar-input"
              type="number"
              value={jaar}
              onChange={e => setJaar(e.target.value)}
              min={2020}
              max={2099}
            />
          </div>
        </div>
      </div>

      {/* Two sections side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Sectie 1: Voorraadmutatie */}
        <div className="rounded-2xl p-5 border" style={{ background: '#1a1a1a', borderColor: '#2a2a2a' }} data-testid="sectie-voorraadmutatie">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full" style={{ background: '#e91e8c' }} />
            <h2 className="text-sm font-bold text-white tracking-tight">Sectie 1 — Voorraadmutatie</h2>
          </div>
          <p className="text-xs text-gray-500 mb-5 ml-4">Kostprijs uit Shopify</p>

          <div className="mb-5">
            <Label className="text-xs text-gray-400 mb-1.5 block">Bedrag (kostprijs)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-mono text-[#e91e8c]">&euro;</span>
              <Input
                data-testid="voorraad-kostprijs-input"
                value={kostprijs}
                onChange={e => setKostprijs(e.target.value)}
                placeholder="0,00"
                className="pl-8 font-mono bg-[#111] border-[#333] text-white placeholder:text-gray-600"
              />
            </div>
          </div>

          {parseBedrag(kostprijs) > 0 && (
            <div className="rounded-xl p-3 space-y-2" style={{ background: '#111' }}>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">3500 Webshop inkopen</span>
                <span className="font-mono text-green-400">D &euro;{fmtEur(round2(parseBedrag(kostprijs)))}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">1901 Voorraden webshop</span>
                <span className="font-mono text-red-400">C &euro;{fmtEur(round2(parseBedrag(kostprijs)))}</span>
              </div>
            </div>
          )}
        </div>

        {/* Sectie 2: Omzetverdeling Webshop */}
        <div className="rounded-2xl p-5 border" style={{ background: '#1a1a1a', borderColor: '#2a2a2a' }} data-testid="sectie-omzetverdeling">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full" style={{ background: '#e91e8c' }} />
            <h2 className="text-sm font-bold text-white tracking-tight">Sectie 2 — Omzetverdeling Webshop</h2>
          </div>
          <p className="text-xs text-gray-500 mb-5 ml-4">B2B + Asten omzet</p>

          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <Label className="text-xs text-gray-400 mb-1.5 block">B2B Omzet</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-mono text-[#e91e8c]">&euro;</span>
                <Input
                  data-testid="b2b-omzet-input"
                  value={b2bOmzet}
                  onChange={e => setB2bOmzet(e.target.value)}
                  placeholder="0,00"
                  className="pl-8 font-mono bg-[#111] border-[#333] text-white placeholder:text-gray-600"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-400 mb-1.5 block">Asten Omzet</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-mono text-[#e91e8c]">&euro;</span>
                <Input
                  data-testid="asten-omzet-input"
                  value={astenOmzet}
                  onChange={e => setAstenOmzet(e.target.value)}
                  placeholder="0,00"
                  className="pl-8 font-mono bg-[#111] border-[#333] text-white placeholder:text-gray-600"
                />
              </div>
            </div>
          </div>

          {(parseBedrag(b2bOmzet) > 0 || parseBedrag(astenOmzet) > 0) && (
            <div className="rounded-xl p-3 space-y-2" style={{ background: '#111' }}>
              {parseBedrag(b2bOmzet) > 0 && (
                <>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">8700 WEBSHOP</span>
                    <span className="font-mono text-green-400">D &euro;{fmtEur(round2(parseBedrag(b2bOmzet)))}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">6700 TRUCKSHO</span>
                    <span className="font-mono text-red-400">C &euro;{fmtEur(round2(parseBedrag(b2bOmzet)))}</span>
                  </div>
                </>
              )}
              {parseBedrag(astenOmzet) > 0 && (
                <>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">8701 WEBSHOP</span>
                    <span className="font-mono text-green-400">D &euro;{fmtEur(round2(parseBedrag(astenOmzet)))}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">8701 ASTEN</span>
                    <span className="font-mono text-red-400">C &euro;{fmtEur(round2(parseBedrag(astenOmzet)))}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview + Export */}
      {hasInput && (
        <div className="space-y-4">
          {/* Toggle preview */}
          <div className="flex items-center justify-between">
            <Button
              data-testid="toggle-preview-btn"
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
              className="text-xs"
            >
              <Eye className="w-3.5 h-3.5 mr-1.5" />
              {showPreview ? 'Verberg preview' : 'Toon preview'}
            </Button>
            <Button
              data-testid="export-memo-btn"
              onClick={handleExport}
              disabled={exporting || journaalRegels.length === 0}
              className="tw1-btn text-xs text-white"
              style={{ background: '#e91e8c' }}
              size="sm"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              {exporting ? 'Exporteren...' : `Download .xls (DB90, ${journaalRegels.length} regels)`}
            </Button>
          </div>

          {/* Preview table */}
          {showPreview && (
            <div className="rounded-2xl overflow-hidden border" style={{ background: '#1a1a1a', borderColor: '#2a2a2a' }} data-testid="preview-table">
              <div className="px-5 py-3 border-b" style={{ borderColor: '#2a2a2a' }}>
                <h3 className="text-sm font-semibold text-white">
                  Preview — Dagboek 90 — {periodeLabel}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">{journaalRegels.length} journaalregels</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ background: '#111' }}>
                      <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Dagboek</th>
                      <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Boekjaar</th>
                      <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Periode</th>
                      <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Grootboek</th>
                      <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Omschrijving</th>
                      <th className="px-4 py-2.5 text-right text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Debet</th>
                      <th className="px-4 py-2.5 text-right text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Credit</th>
                      <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Kostenplaats</th>
                    </tr>
                  </thead>
                  <tbody>
                    {journaalRegels.map((r, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: '#222' }}>
                        <td className="px-4 py-2 text-xs font-mono text-gray-300">90</td>
                        <td className="px-4 py-2 text-xs font-mono text-gray-300">{jaar}</td>
                        <td className="px-4 py-2 text-xs font-mono text-gray-300">{parseInt(maand)}</td>
                        <td className="px-4 py-2 text-xs font-mono text-[#e91e8c]">{r.grootboek}</td>
                        <td className="px-4 py-2 text-xs text-gray-300">{r.omschrijving}</td>
                        <td className="px-4 py-2 text-xs font-mono text-right">
                          {r.debet > 0 ? <span className="text-green-400">{fmtEur(r.debet)}</span> : ''}
                        </td>
                        <td className="px-4 py-2 text-xs font-mono text-right">
                          {r.credit > 0 ? <span className="text-red-400">{fmtEur(r.credit)}</span> : ''}
                        </td>
                        <td className="px-4 py-2 text-xs font-mono text-gray-400">{r.kostenplaats}</td>
                      </tr>
                    ))}
                    {/* Totaal row */}
                    <tr className="border-t-2" style={{ borderColor: '#e91e8c' }}>
                      <td colSpan={5} className="px-4 py-2 text-xs font-semibold text-white">TOTAAL</td>
                      <td className="px-4 py-2 text-xs font-mono font-bold text-right text-green-400">{fmtEur(totaalDebet)}</td>
                      <td className="px-4 py-2 text-xs font-mono font-bold text-right text-red-400">{fmtEur(totaalCredit)}</td>
                      <td className="px-4 py-2">
                        {totaalDebet === totaalCredit && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(30,140,69,0.2)', color: '#4ade80' }}>
                            SLUITEND
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!hasInput && (
        <div className="tw1-card p-12 text-center" data-testid="empty-state">
          <ShoppingBag className="w-10 h-10 mx-auto mb-3" style={{ color: '#e91e8c', opacity: 0.4 }} />
          <p className="text-sm font-medium text-[#0b1220]">Vul bedragen in om journaalregels te genereren</p>
          <p className="text-xs text-[#526071] mt-1">Kostprijs (voorraad) en/of B2B + Asten omzet</p>
        </div>
      )}
    </div>
  );
}
